import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const ToastContext = createContext(null);

const VARIANTS = {
  success: { icon: 'bi-check-circle-fill', color: 'text-success', role: 'status' },
  error: { icon: 'bi-x-circle-fill', color: 'text-destructive', role: 'alert' },
  warning: { icon: 'bi-exclamation-triangle-fill', color: 'text-warning', role: 'status' },
  info: { icon: 'bi-info-circle-fill', color: 'text-primary', role: 'status' },
};

let toastId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [exiting, setExiting] = useState({});
  const timersRef = useRef({});

  const exitTimersRef = useRef({});

  const removeToast = useCallback((id) => {
    setExiting((prev) => ({ ...prev, [id]: true }));
    exitTimersRef.current[id] = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      setExiting((prev) => { const n = { ...prev }; delete n[id]; return n; });
      delete exitTimersRef.current[id];
    }, 250);
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  // Clear all pending timers on unmount.
  useEffect(() => {
    const timers = timersRef.current;
    const exitTimers = exitTimersRef.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
      Object.values(exitTimers).forEach(clearTimeout);
    };
  }, []);

  const addToast = useCallback((message, type = 'info', duration = 3500) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message, type }]);
    timersRef.current[id] = setTimeout(() => removeToast(id), duration);
  }, [removeToast]);

  return (
    <ToastContext.Provider value={addToast}>
      {children}
      {/* Контейнер уведомлений — снизу справа, тулбар не перекрывает */}
      <div className="fixed bottom-0 right-0 z-[200] p-xl flex flex-col gap-md w-full max-w-sm pointer-events-none">
        {toasts.map((toast) => {
          const variant = VARIANTS[toast.type] || VARIANTS.info;
          return (
            <div
              key={toast.id}
              role={variant.role}
              className={
                'pointer-events-auto flex items-start gap-md rounded-md border border-border ' +
                'bg-surface-raised px-lg py-md shadow-lg transition-all duration-200 ' +
                (exiting[toast.id] ? 'opacity-0 translate-x-lg' : 'animate-scale-in')
              }
            >
              <i
                className={`bi ${variant.icon} ${variant.color} text-body-md mt-px`}
                aria-hidden="true"
              />
              <p className="flex-1 text-body-sm text-text">{toast.message}</p>
              <button
                type="button"
                onClick={() => removeToast(toast.id)}
                aria-label="Закрыть уведомление"
                className="-m-sm p-sm cursor-pointer text-text-muted hover:text-text transition-colors duration-150"
              >
                <i className="bi bi-x-lg text-body-xs" aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};
